/**
 * BuscarPedidoModal — buscador de pedidos de Finnegans para vincular a una obra.
 *
 * Lista los pedidos activos que mencionan "Obra" en la descripción (parseo
 * client-side, no depende del sync nuevo), con el detalle de productos de cada
 * uno, para que Ceci seleccione el que corresponde a la obra.
 */

import { useState, useEffect, useMemo } from "react";
import { getPedidosConObraDetectada, getPedidoIdsVinculados } from "../services/obrasFS";
import { syncRapida } from "../../pedidos/services/syncFinnegans";

function formatFinFecha(p) {
  if (p?.finFecha) {
    const d = new Date(`${p.finFecha}T00:00:00`);
    if (!isNaN(d)) return d.toLocaleDateString("es-UY", { day: "2-digit", month: "short" });
  }
  return null;
}

export default function BuscarPedidoModal({ obraNumero, onSelect, onClose, busy, titulo, excluirIds, destino }) {
  const [pedidos, setPedidos] = useState(null); // null = cargando
  const [q, setQ] = useState("");
  const [expandido, setExpandido] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);

  async function cargar() {
    try {
      const [list, vinculados] = await Promise.all([getPedidosConObraDetectada(), getPedidoIdsVinculados()]);
      setPedidos(list.filter(p => {
        if (vinculados.has(p.id) || (excluirIds && excluirIds.has(p.id))) return false;
        // Filtro por destino: los pedidos de Gualeguay llevan "GUALE" en la
        // descripción ("GUALE - OBRA N"); los de R8, no.
        const esGuale = /GUALE/i.test(String(p.descripcion || ""));
        if (destino === "guale") return esGuale;
        if (destino === "r8") return !esGuale;
        return true;
      }));
    } catch (err) {
      console.error("[BuscarPedidoModal]", err);
      setPedidos([]);
    }
  }

  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Dispara el sync manual de Finnegans (para que aparezcan pedidos recién
  // creados sin esperar al sync programado) y recarga la lista.
  async function handleActualizar() {
    if (sincronizando) return;
    setSincronizando(true);
    try {
      await syncRapida();
    } catch (e) {
      console.warn("[BuscarPedidoModal] sync Finnegans", e);
    }
    await cargar();
    setSincronizando(false);
  }

  const visibles = useMemo(() => {
    if (!pedidos) return [];
    const t = q.trim().toLowerCase();
    let list = pedidos;
    if (t) {
      list = list.filter(p =>
        p.id.toLowerCase().includes(t) ||
        (p.cliente || "").toLowerCase().includes(t) ||
        String(p.obraNumero).includes(t) ||
        (p.descripcion || "").toLowerCase().includes(t)
      );
    }
    // El pedido cuya "Obra N" coincide con esta obra va primero
    return [...list].sort((a, b) =>
      Number(b.obraNumero === obraNumero) - Number(a.obraNumero === obraNumero)
    );
  }, [pedidos, q, obraNumero]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 285, padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 14, padding: 18, width: "100%",
          maxWidth: 560, maxHeight: "85vh", display: "flex", flexDirection: "column",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h2 style={{ margin: 0, fontSize: 17 }}>🔍 {titulo || `Buscar pedido de la obra ${obraNumero}`}</h2>
          <button className="btn btn--ghost" onClick={onClose}>✕</button>
        </div>

        <input
          value={q}
          autoFocus
          onChange={e => setQ(e.target.value)}
          placeholder="Filtrar por cliente, número de pedido u obra…"
          style={{
            width: "100%", boxSizing: "border-box", marginBottom: 8,
            padding: "9px 12px", fontSize: 14, border: "1px solid #cbd5e1", borderRadius: 8,
          }}
        />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 11.5, color: "#94a3b8" }}>
            ¿No aparece un pedido recién creado? Actualizá desde Finnegans.
          </span>
          <button
            className="btn btn--outline"
            style={{ fontSize: 12.5, whiteSpace: "nowrap" }}
            disabled={sincronizando}
            onClick={handleActualizar}
          >
            {sincronizando ? "⏳ Actualizando…" : "🔄 Actualizar Finnegans"}
          </button>
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {pedidos === null ? (
            <div style={{ color: "#64748b", textAlign: "center", padding: 24 }}>Buscando pedidos…</div>
          ) : visibles.length === 0 ? (
            <div style={{ color: "#64748b", textAlign: "center", padding: 24, fontSize: 13.5 }}>
              {destino === "guale"
                ? <>No hay pedidos con "GUALE - OBRA N" en la descripción sin vincular.</>
                : <>No hay pedidos con "Obra" en la descripción sin vincular.</>}
              <br />¿Ya se importó el pedido en Finnegans y corrió el sync?
            </div>
          ) : (
            visibles.map(p => {
              const esDeEsta = p.obraNumero === obraNumero;
              const abierto = expandido === p.id;
              const prods = Array.isArray(p.productos) ? p.productos : [];
              return (
                <div
                  key={p.id}
                  style={{
                    border: esDeEsta ? "2px solid #86efac" : "1px solid #e2e8f0",
                    background: esDeEsta ? "#f0fdf4" : "#fff",
                    borderRadius: 10, padding: "10px 12px", marginBottom: 8,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 13.5 }}>
                      <div style={{ fontWeight: 700 }}>
                        <span style={{
                          background: esDeEsta ? "#dcfce7" : "#f1f5f9",
                          color: esDeEsta ? "#15803d" : "#475569",
                          borderRadius: 999, padding: "2px 8px", fontSize: 11.5, marginRight: 6,
                        }}>
                          Obra {p.obraNumero}{esDeEsta ? " ✓" : ""}
                        </span>
                        {p.cliente || "—"}
                      </div>
                      <div style={{ color: "#64748b", fontSize: 12.5, marginTop: 3 }}>
                        {p.id}
                        {formatFinFecha(p) ? ` · ${formatFinFecha(p)}` : ""}
                      </div>
                      {p.descripcion && (
                        <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>{p.descripcion}</div>
                      )}
                    </div>
                    <button
                      className="btn"
                      disabled={busy}
                      onClick={() => onSelect(p)}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      Vincular
                    </button>
                  </div>

                  {prods.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <button
                        className="btn btn--ghost"
                        style={{ fontSize: 12.5, padding: "3px 8px" }}
                        onClick={() => setExpandido(abierto ? null : p.id)}
                      >
                        {abierto ? "▲ Ocultar productos" : `▼ Ver productos (${prods.length})`}
                      </button>
                      {abierto && (
                        <div style={{
                          marginTop: 6, background: "#f8fafc", borderRadius: 8,
                          padding: "8px 10px", maxHeight: 180, overflowY: "auto",
                        }}>
                          {prods.map((it, i) => (
                            <div key={i} style={{ display: "flex", gap: 8, fontSize: 12.5, padding: "3px 0", borderBottom: i < prods.length - 1 ? "1px solid #eef2f7" : "none" }}>
                              <span style={{ fontWeight: 600, minWidth: 34, textAlign: "right" }}>
                                {it.cant ?? it.cantidad ?? "—"}×
                              </span>
                              <span style={{ color: "#475569" }}>
                                {it.desc || it.descripcion || it.nombre || it.cod || "—"}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
